/**
 * Shared label generation utilities for reporting tables
 *
 * SINGLE SOURCE OF TRUTH for all label generation, type suffix stripping,
 * field prefix handling, and field grouping across the tio-reporting addon.
 *
 * Used by:
 * - OpenSearchFieldMapper.formatFieldLabel (delegates here)
 * - OpenSearchFieldMapper.getFieldGroup (delegates here)
 * - OpenSearchFieldMapper.removeFieldTypeSuffixes (delegates here)
 * - FieldConfigService.formatLabel (delegates here)
 * - FieldConfigService.inferGroup (delegates here)
 * - Table components for column headers
 */

// =============================================================================
// Type Suffix Stripping
// =============================================================================

/**
 * Strip OpenSearch type suffixes from field names.
 * Handles .keyword dot notation and camelCase type indicators.
 *
 * @example
 * stripTypeSuffixes('employeeNameKeyword') // 'employeeName'
 * stripTypeSuffixes('createdAtDate') // 'createdAt'
 * stripTypeSuffixes('name.keyword') // 'name'
 * stripTypeSuffixes('isActiveBoolean') // 'isActive'
 */
export const stripTypeSuffixes = (fieldName: string): string => {
  return (
    fieldName
      // Remove .keyword suffix first (for text fields with keyword subfields)
      .replace(/\.keyword$/, '')
      // Remove camelCase type suffixes
      .replace(/Keyword$/i, '')
      .replace(/Text$/i, '')
      .replace(/Date$/i, '')
      .replace(/Boolean$/i, '')
      .replace(/Numeric$/i, '')
      .replace(/Integer$/i, '')
      .replace(/Long$/i, '')
      .replace(/Float$/i, '')
      .replace(/Double$/i, '')
      .replace(/Short$/i, '')
      .replace(/Byte$/i, '')
      .replace(/Nested$/i, '')
  );
};

// =============================================================================
// TAS Prefix Handling
// =============================================================================

/** TAS prefix patterns ordered by specificity (most specific first) */
const TAS_PREFIX_CONTEXT: { pattern: RegExp; context: string }[] = [
  { pattern: /^tasProgramTemplateField/i, context: 'Program' },
  { pattern: /^tasProgramInstanceField/i, context: 'Program' },
  { pattern: /^tasProgramInstance/i, context: 'Program' },
  { pattern: /^tasProgramTemplate/i, context: 'Program' },
  { pattern: /^tasApplicationField/i, context: 'Application' },
  { pattern: /^tasApplication/i, context: 'Application' },
  { pattern: /^tasCourseField/i, context: 'Course' },
  { pattern: /^tasCourse/i, context: 'Course' },
  { pattern: /^tas/i, context: '' },
];

/** Strip TAS prefixes from a field name, returning only the base name */
const stripTasPrefixes = (fieldName: string): string => {
  for (const { pattern } of TAS_PREFIX_CONTEXT) {
    if (pattern.test(fieldName)) {
      return fieldName.replace(pattern, '');
    }
  }
  return fieldName;
};

/** Detect entity context (e.g. 'Program', 'Application') from a TAS-prefixed field name */
const detectEntityContext = (fieldName: string): string => {
  for (const { pattern, context } of TAS_PREFIX_CONTEXT) {
    if (pattern.test(fieldName)) {
      return context;
    }
  }
  return '';
};

// =============================================================================
// Special Label Mappings
// =============================================================================

/**
 * Custom label mappings for fields that don't follow standard naming conventions.
 * Keys are lowercase for case-insensitive lookup.
 * Checked at different stages by different functions:
 * - generateSmartLabel checks full key (e.g., 'paymentLast') before prefix stripping
 * - generateColumnLabel checks stripped key (e.g., 'last') after prefix stripping
 */
const SPECIAL_LABEL_MAP: Record<string, string> = {
  paymentlast: 'Date',
  paymentfirst: 'Date',
  last: 'Date',
  first: 'Date',
};

// =============================================================================
// Field Grouping
// =============================================================================

/**
 * Get the group/category for a field name (for organizing column selection UI).
 * Handles both camelCase (API response) and snake_case (OpenSearch index) field names.
 *
 * @example
 * getFieldGroup('tasProgramTemplateFieldProgramNameKeyword') // 'Program Details'
 * getFieldGroup('tasApplicationStateKeyword') // 'Application'
 * getFieldGroup('employeeNameKeyword') // 'Employee'
 * getFieldGroup('paymentAmountInteger') // 'Payments'
 */
export const getFieldGroup = (fieldName: string): string => {
  // camelCase prefix matching (most specific first)
  if (/^tasProgramTemplateField/i.test(fieldName)) return 'Program Details';
  if (/^tasProgramInstance/i.test(fieldName)) return 'Program Instance';
  if (/^tasProgramTemplate/i.test(fieldName)) return 'Program Template';
  if (/^tasApplication/i.test(fieldName)) return 'Application';
  if (/^tasCourse/i.test(fieldName)) return 'Courses';

  // snake_case prefix matching (for OpenSearch field names)
  if (/^tas_program_template_field/i.test(fieldName)) return 'Program Details';
  if (/^tas_program_instance/i.test(fieldName)) return 'Program Instance';
  if (/^tas_program_template/i.test(fieldName)) return 'Program Template';
  if (/^tas_application/i.test(fieldName)) return 'Application';
  if (/^tas_course/i.test(fieldName)) return 'Courses';

  // Entity prefix matching (works for both camelCase and snake_case)
  if (/payment/i.test(fieldName)) return 'Payments';
  if (/employee/i.test(fieldName)) return 'Employee';
  if (/company/i.test(fieldName)) return 'Company';

  // Generic substring matching (fallback)
  if (/program/i.test(fieldName)) return 'Program';
  if (/application/i.test(fieldName)) return 'Application';
  if (/course/i.test(fieldName)) return 'Courses';

  return 'Other';
};

// =============================================================================
// Label Generation
// =============================================================================

/** Fields that are common across entities and need context to disambiguate */
const AMBIGUOUS_FIELDS = [
  'id',
  'state',
  'name',
  'createdat',
  'created',
  'updatedat',
  'updated',
  'status',
  'type',
];

/**
 * Auto-generates human-readable labels from field names.
 * Handles both camelCase and snake_case input, converts to Title Case.
 *
 * @example
 * autoGenerateLabel('employeeName') // 'Employee Name'
 * autoGenerateLabel('employee_name') // 'Employee Name'
 * autoGenerateLabel('id') // 'Id'
 */
export const autoGenerateLabel = (fieldName: string) => {
  // Handle camelCase: insert spaces before capital letters
  let spaced = fieldName.replace(/([A-Z])/g, ' $1').trim();

  // Handle snake_case and kebab-case: replace separators with spaces
  spaced = spaced.replace(/[_-]/g, ' ');

  // Capitalize first letter of each word
  return spaced
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Generates smart labels for columns that keep necessary context
 * to disambiguate similar fields (e.g., "Program: Name" vs "Application: Name")
 *
 * Removes TAS metadata suffixes and prefixes, then adds back context for
 * ambiguous field names (id, state, name, etc.)
 *
 * @example
 * generateSmartLabel('tasProgramInstanceFieldNameKeyword')
 * // Returns: 'Program: Name' (stripped prefix and Keyword suffix, added context)
 *
 * generateSmartLabel('employeeNameKeyword')
 * // Returns: 'Employee Name' (stripped Keyword suffix, detected ambiguous 'Name', added context)
 *
 * generateSmartLabel('tasApplicationFieldCoursesBeginDateDate')
 * // Returns: 'Courses Begin Date' (stripped prefix and type suffixes, not ambiguous)
 */
export const generateSmartLabel = (columnKey: string) => {
  const cleanKey = stripTypeSuffixes(columnKey);

  // Special case mappings (checked before prefix stripping)
  const specialLabel = SPECIAL_LABEL_MAP[cleanKey.toLowerCase()];
  if (specialLabel) {
    return specialLabel;
  }

  // Strip TAS prefixes to get the base field name
  const baseName = stripTasPrefixes(cleanKey);

  // Payment fields always get "Payment: " prefix for consistency
  if (cleanKey.toLowerCase().startsWith('payment')) {
    const paymentBaseName = baseName.replace(/^payment/i, '');
    return 'Payment: ' + autoGenerateLabel(paymentBaseName);
  }

  // Ambiguous fields need entity context to disambiguate
  const isAmbiguous = AMBIGUOUS_FIELDS.some((field) => baseName.toLowerCase() === field);

  if (isAmbiguous) {
    const context = detectEntityContext(cleanKey);
    if (context) {
      return context + ': ' + autoGenerateLabel(baseName);
    }
    // Check for employee context (not a TAS prefix)
    if (/employee/i.test(cleanKey)) {
      return 'Employee: ' + autoGenerateLabel(baseName);
    }
  }

  // Non-ambiguous fields use clean label without any prefix
  return autoGenerateLabel(baseName);
};

/**
 * Generate label for column selector UI — strips all prefixes, no entity context.
 * Used by OpenSearchFieldMapper.formatFieldLabel (default mode).
 *
 * @example
 * generateColumnLabel('tasProgramTemplateFieldProgramNameKeyword') // 'Program Name'
 * generateColumnLabel('tasApplicationStateKeyword') // 'State'
 * generateColumnLabel('paymentAmountInteger') // 'Amount'
 * generateColumnLabel('paymentLastDate') // 'Date'
 */
export const generateColumnLabel = (fieldName: string): string => {
  const clean = stripTypeSuffixes(fieldName);

  // Strip TAS prefixes, then payment/field prefixes
  let stripped = stripTasPrefixes(clean);
  stripped = stripped.replace(/^payment/i, '').replace(/^field/i, '');

  // Check for special label mapping (e.g., 'last' → 'Date')
  const specialLabel = SPECIAL_LABEL_MAP[stripped.toLowerCase()];
  if (specialLabel) return specialLabel;

  return autoGenerateLabel(stripped);
};

/**
 * Generate label with entity context prefix — adds "Program: ", "Application: " etc.
 * Used by OpenSearchFieldMapper.formatFieldLabel with keepContext=true.
 *
 * Always adds context prefix for TAS-prefixed fields (unlike generateSmartLabel
 * which only adds context for ambiguous field names).
 *
 * @example
 * generateContextualLabel('tasProgramTemplateFieldProgramNameKeyword') // 'Program: Program Name'
 * generateContextualLabel('tasApplicationStateKeyword') // 'Application: State'
 * generateContextualLabel('tasCourseNameKeyword') // 'Course: Name'
 */
export const generateContextualLabel = (fieldName: string): string => {
  const clean = stripTypeSuffixes(fieldName);
  let contextPrefix = '';
  let baseName = clean;

  // Detect context from TAS prefixes
  const entityContext = detectEntityContext(clean);
  if (entityContext) {
    contextPrefix = entityContext + ': ';
    baseName = stripTasPrefixes(clean);
  } else if (/^payment/i.test(clean)) {
    contextPrefix = 'Payment: ';
    baseName = clean.replace(/^payment/i, '') || clean;
  } else {
    // For fields without explicit prefixes, check for ambiguous names
    const isAmbiguous = AMBIGUOUS_FIELDS.some((f) => clean.toLowerCase() === f);

    if (isAmbiguous) {
      // Try to infer context from the original field name
      if (/program/i.test(fieldName)) {
        contextPrefix = 'Program: ';
      } else if (/application/i.test(fieldName)) {
        contextPrefix = 'Application: ';
      } else if (/course/i.test(fieldName)) {
        contextPrefix = 'Course: ';
      } else if (/payment/i.test(fieldName)) {
        contextPrefix = 'Payment: ';
      } else if (/employee/i.test(fieldName)) {
        contextPrefix = 'Employee: ';
      }
    }
  }

  // Check for special label mapping
  const specialLabel = SPECIAL_LABEL_MAP[baseName.toLowerCase()];
  if (specialLabel) {
    return contextPrefix + specialLabel;
  }

  return contextPrefix + autoGenerateLabel(baseName);
};
